import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { APP_NAME } from '../constants';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable, HttpsCallableResult } from 'firebase/functions';
import { db } from '../firebase/config';
import { getCurrentUser, User } from '../services/authService';
import { canManageStaff, getPermissionErrorMessage, getAvailableRoles, canAssignRole, getRoleAssignmentErrorMessage, getUserRole } from '../services/staffPermissionService';
import { uploadUserPhoto } from '../services/storageService';
import TopBar from '../components/TopBar';
import Input from '../components/Input';
import PhoneInput from '../components/PhoneInput';
import Button from '../components/Button';
import defaultProfilePic from '../assets/default-profile.jpg';
import ConfirmationModal from '../components/ConfirmationModal';

interface StaffMember {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  businessId: string;
  photoUrl?: string;
}

interface EditStaffData {
  staffId: string;
  businessId: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  role?: string;
  photoUrl?: string;
}

interface RemoveStaffData {
  staffId: string;
  businessId: string;
}

interface EditStaffResult {
  success: boolean;
  message?: string;
}

interface RemoveStaffResult {
  success: boolean;
  message?: string;
}

interface Business {
  id: string;
  name: string;
}

const Container = styled.div`
  padding: 0 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (min-width: 768px) {
    padding: 0 40px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
`;

const PageTitle = styled.h1`
  font-size: 38px;
  font-weight: 800;
  margin-bottom: 16px;
  color: #333;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const Breadcrumbs = styled.div`
  display: flex;
  margin-bottom: 20px;
  font-size: 14px;
  color: #555;
  width: 100%;
  overflow-x: hidden;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const BreadcrumbLink = styled.span`
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

const BreadcrumbSeparator = styled.span`
  margin: 0 8px;
`;

const BreadcrumbText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100px;

  @media (max-width: 480px) {
    max-width: 80px;
  }
`;

const FormContainer = styled.div`
  background-color: white;
  border-radius: 24px;
  padding: 32px;
  margin-bottom: 24px;
  width: 100%;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const FormTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 24px;
  color: #333;
`;

const NamePhotoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 20px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
  }
`;

const PhotoContainer = styled.div`
  flex-shrink: 0;
  order: 1;

  @media (min-width: 768px) {
    order: 1;
  }
`;

const ProfilePhoto = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);

  &::after {
    content: 'Change Photo';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    font-size: 12px;
    padding: 5px;
    text-align: center;
    opacity: 0;
    transition: opacity 0.3s;
  }

  &:hover::after {
    opacity: 1;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const NameContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  order: 2;
`;

const NameRow = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0;
  }
`;

const FormColumn = styled.div`
  flex: 1;
`;

const FormSection = styled.div`
  margin-bottom: 20px;
`;

const PhoneSection = styled.div`
  margin-top: 4px;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const ButtonContainer = styled.div`
  margin-top: 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  @media (max-width: 480px) {
    flex-direction: column-reverse;
    align-items: stretch;
  }
`;

const RoleContainer = styled.div`
  margin-bottom: 20px;
`;

const RoleLabel = styled.div`
  font-size: 14px;
  margin-bottom: 8px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
`;

const RoleSelect = styled.select`
  width: 100%;
  padding: 14px;
  font-size: 16px;
  border: none;
  border-radius: 12px;
  background: #f5f5f5;
  font-family: 'Montserrat', sans-serif;
  outline: none;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  color: #333;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 14px center;
  background-size: 16px;
`;

const ErrorMessage = styled.div`
  color: #e53935;
  padding: 16px;
  background-color: #ffebee;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 14px;
`;

const SuccessMessage = styled.div`
  color: #43a047;
  padding: 16px;
  background-color: #e8f5e9;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 14px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px;
`;

const PermissionErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 60px 20px;
  background-color: #f9f9f9;
  border-radius: 12px;
  margin-top: 40px;
`;

const PermissionErrorTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
`;

const PermissionErrorMessage = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 32px;
  max-width: 400px;
  line-height: 1.5;
`;

const SelfEditWarning = styled.div`
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  color: #856404;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 14px;
`;

const EditStaffPage: React.FC = () => {
  const navigate = useNavigate();
  const { businessId, staffId } = useParams<{ businessId: string; staffId: string }>();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [initialStaffData, setInitialStaffData] = useState<StaffMember | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  // Form fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState('staff');
  const [photoUrl, setPhotoUrl] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [permissionError, setPermissionError] = useState<string>('');
  const [isSelfEdit, setIsSelfEdit] = useState(false);
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Firebase Functions
  const functions = getFunctions();
  const editStaffAccountCallable = httpsCallable<EditStaffData, EditStaffResult>(functions, 'editStaffAccount');
  const removeStaffMemberCallable = httpsCallable<RemoveStaffData, RemoveStaffResult>(functions, 'removeStaffMember');

  useEffect(() => {
    const fetchData = async () => {
      if (!businessId || !staffId) {
        console.error("Missing businessId or staffId");
        navigate('/businesses');
        return;
      }
      
      try {
        setLoading(true);
        setError('');
        setPermissionError('');
        
        const user = await getCurrentUser();
        setCurrentUser(user);

        if (!user?.id) {
          navigate('/login');
          return;
        }

        // Check if user has permission to manage staff
        const hasPermission = await canManageStaff(user.id, businessId);
        if (!hasPermission) {
          setPermissionError(getPermissionErrorMessage('edit-staff'));
          setLoading(false);
          return;
        }

        // Get available roles for this user
        const roles = await getAvailableRoles(user.id, businessId);
        setAvailableRoles(roles);
        
        const businessDocRef = doc(db, 'businesses', businessId);
        const businessDoc = await getDoc(businessDocRef);
        if (businessDoc.exists()) {
          setBusiness({ id: businessDoc.id, name: businessDoc.data().name });
        } else {
          throw new Error("Business not found");
        }
        
        const staffDocRef = doc(db, 'staff', staffId);
        const staffDoc = await getDoc(staffDocRef);
        if (staffDoc.exists()) {
           const data = staffDoc.data();
           const fetchedStaffData: StaffMember = {
               id: staffDoc.id,
               userId: data.userId,
               firstName: data.firstName,
               lastName: data.lastName,
               email: data.email,
               phone: data.phone,
               role: data.role,
               businessId: data.businessId,
               photoUrl: data.photoUrl || '',
           };
 
           if (fetchedStaffData.businessId !== businessId) {
              throw new Error("Staff member does not belong to this business.");
           }

           // Check if user is trying to edit themselves
           if (fetchedStaffData.userId === user.id) {
             setIsSelfEdit(true);
           }
 
           setInitialStaffData(fetchedStaffData);
           setFirstName(fetchedStaffData.firstName);
           setLastName(fetchedStaffData.lastName);
           setEmail(fetchedStaffData.email);
           setPhone(fetchedStaffData.phone);
           setRole(fetchedStaffData.role);
           setPhotoUrl(fetchedStaffData.photoUrl || '');
 
         } else {
           throw new Error("Staff member not found");
         }
      } catch (err: any) {
        console.error('Error fetching data:', err);
        setError(`Failed to load data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [businessId, staffId, navigate]);

  const handleNavigateToMyAccount = () => navigate('/dashboard');
  const handleNavigateToMyBusinesses = () => navigate('/businesses');
  const handleNavigateToSettings = () => navigate(`/business/${businessId}/settings`);
  const handleNavigateToStaff = () => navigate(`/business/${businessId}/staff`);

  const handlePhotoClick = () => {
    if (!isSelfEdit && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isSelfEdit) return;
    
    const files = e.target.files;
    if (files && files[0]) {
      setPhotoFile(files[0]);
      setPhotoUrl(URL.createObjectURL(files[0]));
    }
  };

  const handleSaveChanges = async () => {
    if (!businessId || !staffId || !initialStaffData) return;
    
    setError(''); 
    setSuccess('');

    if (isSelfEdit) {
      setError('You cannot edit your own staff record. Please contact your business owner or manager.');
      return;
    }

    if (!firstName.trim() || !lastName.trim() || !phone.trim()) {
      setError('Please fill in first name, last name, and phone.');
      return;
    }

    // Check if user can assign the selected role
    const currentUser = await getCurrentUser();
    if (currentUser?.id) {
      const canAssign = await canAssignRole(currentUser.id, businessId, role as any);
      if (!canAssign) {
        const userRole = await getUserRole(currentUser.id, businessId);
        setError(getRoleAssignmentErrorMessage(userRole, role as any));
        return;
      }
    }

    setSaving(true);

    try {
      let finalPhotoUrl = initialStaffData.photoUrl || '';
      if (photoFile) {
        finalPhotoUrl = await uploadUserPhoto(initialStaffData.userId, photoFile);
      }

      const editData: EditStaffData = {
        staffId, 
        businessId, 
        firstName, 
        lastName, 
        phone, 
        role,
        photoUrl: finalPhotoUrl || undefined,
      };

      const result = await editStaffAccountCallable(editData);

      if (result.data.success) {
        setSuccess(result.data.message || 'Staff member updated successfully!');
        setPhotoFile(null);
        setInitialStaffData(prev => prev ? {...prev, ...editData, photoUrl: finalPhotoUrl} : null);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.data.message || 'Failed to update staff member.');
      }
    } catch (error: any) {
       console.error('Error calling editStaffAccount function:', error);
       if (error.code === 'functions/permission-denied') setError('Permission denied.');
       else if (error.code === 'functions/invalid-argument') setError(`Invalid data: ${error.message || 'Check fields.'}`);
       else if (error.code === 'functions/not-found') setError('Staff or business not found.');
       else setError('An unexpected error occurred.');
    } finally {
      setSaving(false);
    }
  };

  const triggerRemoveConfirmation = () => { 
    if (isSelfEdit) {
      setError('You cannot remove yourself from the team. Please contact your business owner or manager.');
      return;
    }
    
    setError(''); 
    setSuccess(''); 
    setShowRemoveConfirm(true); 
  };

  const handleConfirmRemove = async () => {
    if (!businessId || !staffId || isSelfEdit) return;
    
    setShowRemoveConfirm(false); 
    setRemoving(true);

    try {
      const removeData: RemoveStaffData = { staffId, businessId };
      const result = await removeStaffMemberCallable(removeData);

      if (result.data.success) {
        setSuccess(result.data.message || 'Staff member removed successfully!');
        setTimeout(() => navigate(`/business/${businessId}/staff`), 2000);
      } else {
        setError(result.data.message || 'Failed to remove staff member.');
      }
    } catch (error: any) {
       console.error('Error calling removeStaffMember function:', error);
       if (error.code === 'functions/permission-denied') setError('Permission denied.');
       else if (error.code === 'functions/not-found') setError('Staff or business not found.');
       else setError('An unexpected error occurred.');
    } finally {
      setRemoving(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <ContentWrapper>
          <TopBar user={currentUser} />
          <LoadingContainer>Loading Team Details...</LoadingContainer>
        </ContentWrapper>
      </Container>
    );
  }

  // Handle permission error
  if (permissionError) {
    return (
      <>
        <Helmet>
          <title>{APP_NAME} - Access Denied</title>
        </Helmet>
        <Container>
          <ContentWrapper>
            <TopBar user={currentUser} />
            <PermissionErrorContainer>
              <PermissionErrorTitle>Access Denied</PermissionErrorTitle>
              <PermissionErrorMessage>{permissionError}</PermissionErrorMessage>
              <Button 
                text="Back to Team" 
                onClick={() => navigate(`/business/${businessId}/staff`)}
                bgColor="#000"
                textColor="#fff"
                width="auto"
                minWidth="180px"
              />
            </PermissionErrorContainer>
          </ContentWrapper>
        </Container>
      </>
    );
  }

  if (error && !initialStaffData) { 
    return (
      <Container>
        <ContentWrapper>
          <TopBar user={currentUser} />
          <PageTitle>Edit Team Member</PageTitle>
          <Breadcrumbs>
            <BreadcrumbLink onClick={handleNavigateToStaff}>Staff</BreadcrumbLink>
          </Breadcrumbs>
          <ErrorMessage>{error}</ErrorMessage>
        </ContentWrapper>
      </Container>
    );
  }

  if (!initialStaffData || !business) {
    return <div>Team member or business data could not be loaded.</div>;
  }

  return (
    <>
      <Helmet>
        <title>{APP_NAME} - Edit Staff</title>
        <meta name="description" content={`${APP_NAME} - Edit Staff`} />
      </Helmet>
      <Container>
        <ContentWrapper>
          <TopBar user={currentUser} />
        
          <PageTitle>Edit Team Member</PageTitle>
        
          <Breadcrumbs>
            <BreadcrumbLink onClick={handleNavigateToMyAccount}>My Account</BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbLink onClick={handleNavigateToMyBusinesses}>My Businesses</BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbLink onClick={handleNavigateToSettings}>
              <BreadcrumbText>{business?.name || 'Business'}</BreadcrumbText>
            </BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbLink onClick={handleNavigateToStaff}>Staff</BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <span>Edit: {initialStaffData.firstName} {initialStaffData.lastName}</span>
          </Breadcrumbs>

          {isSelfEdit && (
            <SelfEditWarning>
              <strong>Note:</strong> You are viewing your own staff record. You cannot edit your own information or remove yourself from the team. Please contact your business owner or manager for any changes.
            </SelfEditWarning>
          )}

          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          <FormContainer>
            <FormTitle>Edit user details</FormTitle>
            
            <NamePhotoSection>
              <PhotoContainer>
                <ProfilePhoto onClick={handlePhotoClick} style={{ cursor: isSelfEdit ? 'not-allowed' : 'pointer' }}>
                  <img src={photoUrl || defaultProfilePic} alt="Profile" onError={(e) => { (e.target as HTMLImageElement).src = defaultProfilePic; }} />
                </ProfilePhoto>
                <HiddenFileInput type="file" accept="image/*" ref={fileInputRef} onChange={handlePhotoChange} disabled={isSelfEdit} />
              </PhotoContainer>
              
              <NameContainer>
                <NameRow>
                  <FormColumn>
                    <Input 
                      type="text" 
                      value={firstName} 
                      onChange={(e) => !isSelfEdit && setFirstName(e.target.value)} 
                      placeholder="First Name" 
                      label="First Name" 
                      name="firstName" 
                      required 
                      secondary 
                      readOnly={isSelfEdit}
                    />
                  </FormColumn>
                  <FormColumn>
                    <Input 
                      type="text" 
                      value={lastName} 
                      onChange={(e) => !isSelfEdit && setLastName(e.target.value)} 
                      placeholder="Last Name" 
                      label="Last Name" 
                      name="lastName" 
                      required 
                      secondary 
                      readOnly={isSelfEdit}
                    />
                  </FormColumn>
                </NameRow>
                
                <PhoneSection>
                  <PhoneInput 
                    value={phone} 
                    onChange={(value) => !isSelfEdit && setPhone(value || '')} 
                    name="phone" 
                    label="Phone Number" 
                    required 
                    secondary 
                    disabled={isSelfEdit}
                  />
                </PhoneSection>
              </NameContainer>
            </NamePhotoSection>
            
            <FormSection>
              <Input 
                type="email" 
                value={email} 
                onChange={() => {}} 
                placeholder="Email Address" 
                label="Email" 
                name="email" 
                readOnly 
                secondary 
              />
            </FormSection>
            
            <RoleContainer>
              <RoleLabel>Role</RoleLabel>
              <RoleSelect 
                value={role} 
                onChange={(e) => !isSelfEdit && setRole(e.target.value)}
                disabled={isSelfEdit}
              >
                {availableRoles.map((roleOption) => (
                  <option key={roleOption} value={roleOption}>
                    {roleOption.charAt(0).toUpperCase() + roleOption.slice(1)}
                  </option>
                ))}
              </RoleSelect>
            </RoleContainer>
          </FormContainer>
          
          <ButtonContainer>
            <Button 
              text="Remove" 
              onClick={triggerRemoveConfirmation} 
              loading={removing} 
              outline={true} 
              textColor="#e53935" 
              bgColor="#e53935" 
              width="auto" 
              minWidth="150px"
              disabled={isSelfEdit}
            />
            <Button 
              text="Save" 
              onClick={handleSaveChanges} 
              loading={saving} 
              bgColor="#000" 
              textColor="#fff" 
              width="auto" 
              minWidth="150px"
              disabled={isSelfEdit}
            />
          </ButtonContainer>
        </ContentWrapper>
        
        <ConfirmationModal 
          isOpen={showRemoveConfirm} 
          title="Confirm Removal" 
          message={`Are you sure you want to remove ${initialStaffData?.firstName || ''} ${initialStaffData?.lastName || ''} from the team? Their user account will not be deleted.`} 
          confirmText="Remove" 
          cancelText="Cancel" 
          onConfirm={handleConfirmRemove} 
          onCancel={() => setShowRemoveConfirm(false)} 
          isConfirming={removing} 
          confirmButtonVariant="danger" 
        />
      </Container>
    </>
  );
};

export default EditStaffPage;
